// pages/SellPhonePage.js — "Sell Your Phone" enquiry wizard (2 easy steps → WhatsApp)
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaWhatsapp,
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaCamera,
  FaTimes,
  FaGem,
  FaThumbsUp,
  FaRegMeh,
  FaTools,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import './SellPhonePage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.jollybaba.in';
const WHATSAPP_NUMBER = process.env.REACT_APP_SELL_WHATSAPP_NUMBER || '917891011841';

const BRANDS = ['Apple', 'Samsung', 'OnePlus', 'Vivo', 'Oppo', 'Realme', 'Xiaomi', 'Motorola', 'Nothing', 'Other'];
const STORAGES = ['32 GB', '64 GB', '128 GB', '256 GB', '512 GB', '1 TB', 'Not sure'];

const CONDITIONS = [
  { value: 'Like New', icon: FaGem, hint: 'No scratches' },
  { value: 'Good', icon: FaThumbsUp, hint: 'Minor marks' },
  { value: 'Average', icon: FaRegMeh, hint: 'Visible wear' },
  { value: 'Needs Repair', icon: FaTools, hint: 'Damaged / not working' },
];

const STEPS = ['Your phone', 'Condition'];
const MAX_PHOTOS = 4;

// Shrink camera originals (5–10 MB) to a small WebP before upload so the
// submit doesn't crawl on mobile data. Falls back to the original file if
// anything in the pipeline is unsupported — the server re-compresses anyway.
const compressImage = async (file) => {
  try {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      bitmap = await createImageBitmap(file);
    }
    const MAX_DIM = 1600;
    const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.8));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.webp', { type: 'image/webp' });
  } catch {
    return file;
  }
};

// Two rising notes, synthesized on the spot so no audio file is needed.
// Runs inside the submit tap's gesture chain, which keeps autoplay rules happy.
const playSuccessChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === 'suspended') ctx.resume();
    const note = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };
    note(880, 0, 0.2);
    note(1318.5, 0.13, 0.4);
    setTimeout(() => ctx.close(), 1500);
  } catch {
    /* sound is a nicety, never block the flow */
  }
};

const SellPhonePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [brand, setBrand] = useState('');
  const [otherBrand, setOtherBrand] = useState('');
  const [model, setModel] = useState('');
  const [storage, setStorage] = useState('');
  const [condition, setCondition] = useState('');
  const [expectedPrice, setExpectedPrice] = useState('');
  const [photos, setPhotos] = useState([]); // [{ file, preview }]
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { enquiryId, photoUrls }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, result]);

  useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.preview)), [photos]);

  const effectiveBrand = brand === 'Other' ? otherBrand.trim() : brand;

  const step1Valid = effectiveBrand && model.trim();
  const step2Valid = !!condition;

  const addPhotos = async (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    const compressed = await Promise.all(incoming.map(compressImage));
    setPhotos((prev) =>
      [...prev, ...compressed.map((file) => ({ file, preview: URL.createObjectURL(file) }))].slice(0, MAX_PHOTOS)
    );
  };

  const removePhoto = (index) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildWhatsAppText = (enquiry) => {
    const lines = [
      `Hi JollyBaba! I want to sell my phone. (Ref: ${enquiry.enquiryId})`,
      '',
      `*Phone:* ${effectiveBrand} ${model.trim()}${storage && storage !== 'Not sure' ? `, ${storage}` : ''}`,
      `*Condition:* ${condition}`,
    ];
    if (expectedPrice) {
      lines.push(`*Expecting:* ₹${Number(expectedPrice).toLocaleString('en-IN')}`);
    }
    if (enquiry.photoUrls?.length) {
      lines.push('', '*Photos:* (links valid for 7 days)', ...enquiry.photoUrls);
    }
    return lines.join('\n');
  };

  const whatsAppLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

  const fallbackWhatsAppText = () =>
    `Hi JollyBaba! I want to sell my ${effectiveBrand} ${model.trim()} (${condition || 'used'}).`;

  const handleSubmit = async () => {
    if (!step2Valid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('brand', effectiveBrand);
      formData.append('model', model.trim());
      if (storage && storage !== 'Not sure') formData.append('storage', storage);
      formData.append('condition', condition);
      if (expectedPrice) formData.append('expectedPrice', expectedPrice);
      photos.forEach((p) => formData.append('photos', p.file));

      const res = await axios.post(`${API_BASE}/api/sell-enquiries`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      playSuccessChime();
      // Hand over to WhatsApp — the customer's number reaches us there.
      // The pause lets the tick animation and chime land first.
      const link = whatsAppLink(buildWhatsAppText(res.data));
      setTimeout(() => {
        window.location.href = link;
      }, 1800);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          'Could not send your request right now. Please try again, or message us directly on WhatsApp below.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Success screen (fallback if WhatsApp doesn't auto-open) ---------- */
  if (result) {
    return (
      <div className="sell-page">
        <SEO title="Request Sent | JollyBaba Mobiles" canonical="https://jollybaba.in/sell" />
        <div className="sell-card sell-success">
          <div className="sell-success-icon">
            <svg className="sell-tick" viewBox="0 0 72 72" aria-hidden="true">
              <circle className="sell-tick-circle" cx="36" cy="36" r="32" />
              <path className="sell-tick-check" d="M22 37.5l9.5 9.5L50 27" />
            </svg>
          </div>
          <h1>Details saved</h1>
          <p className="sell-success-ref">
            Reference <strong>{result.enquiryId}</strong>
          </p>
          <p className="sell-success-note">Opening WhatsApp. Press send to get your price.</p>
          <a
            className="sell-btn sell-btn-whatsapp"
            href={whatsAppLink(buildWhatsAppText(result))}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp /> Open WhatsApp
          </a>
          <button className="sell-link-btn" onClick={() => navigate('/')}>
            Back to the shop
          </button>
        </div>
      </div>
    );
  }

  /* ---------- Wizard ---------- */
  return (
    <div className="sell-page">
      <SEO
        title="Sell Your Old Phone in Pune | JollyBaba Mobiles"
        description="Sell your second hand mobile phone to JollyBaba Mobiles, Deccan, Pune. Tell us about your phone and get our best price on WhatsApp. Free, no obligation."
        keywords="sell old phone Pune, sell used mobile Pune, second hand phone buyers Pune, sell iPhone Pune, sell Samsung Pune"
        canonical="https://jollybaba.in/sell"
      />

      <div className="sell-hero">
        <Link to="/" className="sell-back">
          <FaArrowLeft /> Shop
        </Link>
        <h1>Sell your phone</h1>
        <p>Best price on WhatsApp</p>
      </div>

      <ol className="sell-steps-list" aria-label={`Step ${step + 1} of ${STEPS.length}`}>
        {STEPS.map((s, i) => {
          const done = i < step;
          return (
            <li
              key={s}
              className={`sell-steps-item ${done ? 'done' : ''} ${i === step ? 'active' : ''}`}
            >
              <span className="sell-steps-badge">{done ? <FaCheck /> : i + 1}</span>
              <span className="sell-steps-name">{s}</span>
            </li>
          );
        })}
      </ol>

      <div className="sell-card">
        <div className="sell-step-body" key={step}>
        {step === 0 && (
          <>
            <label className="sell-label" htmlFor="sell-brand">Brand</label>
            <select
              id="sell-brand"
              className="sell-input sell-select"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            >
              <option value="" disabled>Select brand</option>
              {BRANDS.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            {brand === 'Other' && (
              <input
                className="sell-input"
                type="text"
                placeholder="Type the brand name"
                value={otherBrand}
                onChange={(e) => setOtherBrand(e.target.value)}
                maxLength={40}
              />
            )}

            <label className="sell-label" htmlFor="sell-model">Model</label>
            <input
              id="sell-model"
              className="sell-input"
              type="text"
              placeholder="e.g. iPhone 13, Galaxy S21"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              maxLength={80}
            />

            <label className="sell-label" htmlFor="sell-storage">
              Storage <span className="sell-optional">(optional)</span>
            </label>
            <select
              id="sell-storage"
              className="sell-input sell-select"
              value={storage}
              onChange={(e) => setStorage(e.target.value)}
            >
              <option value="">Select storage</option>
              {STORAGES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </>
        )}

        {step === 1 && (
          <>
            <label className="sell-label">Condition</label>
            <div className="sell-condition-grid">
              {CONDITIONS.map(({ value, icon: Icon, hint }) => (
                <button
                  key={value}
                  type="button"
                  className={`sell-condition-card ${condition === value ? 'selected' : ''}`}
                  onClick={() => setCondition(value)}
                >
                  <Icon className="sell-condition-icon" />
                  <span className="sell-condition-title">{value}</span>
                  <span className="sell-condition-hint">{hint}</span>
                </button>
              ))}
            </div>

            <label className="sell-label">
              Photos <span className="sell-optional">(optional)</span>
            </label>
            <div className="sell-photo-row">
              {photos.map((p, i) => (
                <div key={p.preview} className="sell-photo-thumb">
                  <img src={p.preview} alt={`Phone ${i + 1}`} />
                  <button type="button" className="sell-photo-remove" onClick={() => removePhoto(i)} aria-label="Remove photo">
                    <FaTimes />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <button type="button" className="sell-photo-add" onClick={() => fileInputRef.current?.click()}>
                  <FaCamera />
                  <span>Add photo</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  addPhotos(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>

            <label className="sell-label" htmlFor="sell-price">
              Expected price <span className="sell-optional">(optional)</span>
            </label>
            <div className="sell-price-wrap">
              <span className="sell-price-rupee">₹</span>
              <input
                id="sell-price"
                className="sell-input sell-price-input"
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="e.g. 15000"
                value={expectedPrice}
                onChange={(e) => setExpectedPrice(e.target.value)}
              />
            </div>

            {/* Honeypot — hidden from real users */}
            <input type="text" name="website" className="sell-honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            {error && (
              <div className="sell-error">
                {error}
                <a
                  href={whatsAppLink(fallbackWhatsAppText())}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sell-error-link"
                >
                  <FaWhatsapp /> Message us directly instead
                </a>
              </div>
            )}
          </>
        )}
        </div>

        <div className="sell-nav">
          {step > 0 && (
            <button type="button" className="sell-btn sell-btn-ghost" onClick={() => setStep(step - 1)} disabled={submitting}>
              <FaArrowLeft /> Back
            </button>
          )}
          {step < 1 ? (
            <button
              type="button"
              className="sell-btn sell-btn-primary"
              disabled={!step1Valid}
              onClick={() => setStep(step + 1)}
            >
              Continue <FaArrowRight />
            </button>
          ) : (
            <button
              type="button"
              className="sell-btn sell-btn-whatsapp"
              disabled={!step2Valid || submitting}
              onClick={handleSubmit}
            >
              {submitting ? <span className="sell-spinner" aria-label="Sending" /> : <><FaWhatsapp /> Get My Price on WhatsApp</>}
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default SellPhonePage;
